/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Script from 'next/script';
import Router, { useRouter } from 'next/router';
import Dynamic from 'next/dynamic';
import { useUser } from '@auth0/nextjs-auth0/client';

import { storeWrapper } from 'store';
import { useSelector, useDispatch } from 'react-redux';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Grid,
  Snackbar,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ReactHtmlParser from 'react-html-parser';

import Summary from 'components/SummaryComponent';
import Header from 'components/Shared/Header';

import {
  BuildPackageComponent,
  LabelWithDescriptionComponent,
  SchoolBreadcrumbComponent,
  SchoolGalleryComponent,
  SchoolLocationComponent,
  SchoolTestimoniesComponent,
  StepsComponent,
  VideoComponent,
} from 'components/SchoolsComponents';
import SkeletonFull from 'components/SchoolsComponents/SchoolGalleryComponent/SkeletonFull';
import DynamicFaqComponent from 'components/DynamicFaqComponent';
import ScarcityVacancyComponent from 'components/Shared/ScarcityVacancy';

import { createOrder } from 'services/abroadService';
import {
  fetchSchoolDetail,
  fetchSchoolDataFilter,
  getMainSchoolImage,
} from 'services/schoolService';
import { fetchCountries } from 'services/countryService';
import { fetchAccommodations } from 'services/accommodationService';
import { fetchAdditionalServices } from 'services/additionalServiceService';
import { fetchCourses } from 'services/courseService';

import { DefaultMenuNav } from 'mocks/menuNav';
import { abroadFaq } from 'mocks/abroadFaq';

import {
  abroadDateSanitization,
  categoriesSlugInfo,
  categorySlug,
  getSchoolDefaultDates,
} from 'utils/schoolUtils';
import { buildUrl, getSearchParameters } from 'utils/urlUtils';
import {
  getSelectedCourseId,
  selectCheapestCourse,
  selectCheapestCourseByWeeks,
  SelectCourse,
} from 'utils/courseUtils';
import {
  getSelectedAccommodationId,
  getSelectedAccommodationBedroomType,
  selectCheapestAccommodation,
  selectAccommodationById,
  toggleAccommodationsBedroomType,
} from 'utils/accommodationUtils';
import {
  getSelectedServicesIds,
  getSelectedOshc,
} from 'utils/additionalServicesUtils';
import {
  getSelectedInsurancesIds,
  getLocationInsurances,
} from 'utils/insuranceUtils';
import { generateReviewSchema } from 'utils/gtagUtils';

import { buildAddress } from 'utils/locationUtils';
import {
  hasPermissionByRole,
  getSalesExecutiveCode,
} from 'utils/auth/authHelper';
import { formatDate } from 'utils/date';
import {
  UpdateCourse,
  UpdateAccommodation,
  UpdateAdditionalService,
} from 'store/actions/schoolDetailActions';

const LeadFormComponent = Dynamic(
  () => import('components/LeadFormComponent'),
  { ssr: false }
);

const Footer = Dynamic(() => import('components/Footer'), { ssr: true });

function SchoolPage({ params, isSalesExecutive }) {
  const theme = useTheme();
  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const detail = useSelector((store) => store.schoolDetailReducer);
  const dispatch = useDispatch();
  const { user } = useUser();
  const defaultWeeks = new Array(1).fill(2);
  const detailWeeks =
    detail && detail.weeks && detail.weeks.length > 0
      ? detail.weeks
      : defaultWeeks;

  params.startDate = abroadDateSanitization(params.startDate);

  const [dateParams, setDateParams] = useState(
    getSchoolDefaultDates(params, detailWeeks)
  );
  const [isFetching, setIsFetching] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [openGallery, setOpenGallery] = useState(false);
  const [showFullInformation, setShowFullInformation] = useState(false);
  const handleShowFullInformation = () =>
    setShowFullInformation(!showFullInformation);
  const handleShowLoader = (show) => setShowLoader(show);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const defaultErrorMessage =
    'Ocorreu uma instabilidade no sistema, por favor tente mais tarde';
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
  const address = buildAddress(detail.location);
  const [mainImage, setMainImage] = useState('');
  const aboutSchool = ReactHtmlParser(detail.about);

  const title = `${detail?.fantasyName}: Faça seu Intercâmbio no Exterior`;

  const handleOpenGallery = () => setOpenGallery(!openGallery);

  if (detail.isActive) {
    if (
      detail.needAccommodation &&
      getSelectedAccommodationId(detail.accommodationGroups) === 0
    ) {
      selectCheapestAccommodation(detail.accommodationGroups);
    }
  }

  const handleCloseErrorMessage = (_, reason) => {
    if (reason === 'clickaway') return;

    setErrorMessage(defaultErrorMessage);

    setShowErrorMessage(false);
  };

  const createAbroad = async () => {
    handleShowLoader(true);

    try {
      const { accommodationGroups, additionalServices, courses, location } =
        detail;

      const selectedAccommodations = [
        {
          startDate: new Date(dateParams.accommodationStart),
          endDate: new Date(dateParams.accommodationEnd),
          bedroomType: getSelectedAccommodationBedroomType(accommodationGroups),
          id: getSelectedAccommodationId(accommodationGroups),
        },
      ];

      const selectedAdditionals = getSelectedServicesIds(
        additionalServices,
        dateParams.courseWeeks,
        accommodationGroups.some((ag) => ag.selected)
      );

      const selectedOshc = getSelectedOshc(additionalServices);

      const selectedCourse = [
        {
          startDate: new Date(dateParams.courseStart),
          weekQuantity: dateParams.courseWeeks,
          id: getSelectedCourseId(courses),
        },
      ];

      const selectedInsurances = getSelectedInsurancesIds(
        getLocationInsurances(location)
      );

      const requestBody = {
        schoolId: parseInt(params?.id, 10),
        accommodations: selectedAccommodations,
        additionals: selectedAdditionals,
        email: '',
        courses: selectedCourse,
        oshc: selectedOshc,
        insurances: selectedInsurances,
        checkin: false,
        checkout: false,
        step: 0,
        salesExecutiveCode: getSalesExecutiveCode(user),
        deposit: parseFloat(detail.deposit, 10),
      };

      if (!detail.needAccommodation) requestBody.accommodations = [];
      if (
        !accommodationGroups.some((ag) => ag.selected) &&
        additionalServices.some((as) => as.selected && as.hasAccommodation)
      )
        throw new Error();

      const result = await createOrder(requestBody);

      Router.push(`/registration/${result.hash}`);
    } catch (error) {
      setErrorMessage(error.message);
      setShowErrorMessage(true);
      handleShowLoader(false);
    }
  };

  const updatePageOptions = async (
    courseParams,
    accommodationParams,
    additionalServiceParam
  ) => {
    // Course
    const tempCourses = await fetchCourses(courseParams, dispatch);
    if (tempCourses) {
      selectCheapestCourseByWeeks(tempCourses, dateParams.courseWeeks);

      const courseId =
        getSelectedCourseId(detail.courses) ||
        getSelectedCourseId(tempCourses) ||
        params.courseId;
      if (courseId !== undefined)
        SelectCourse(tempCourses, courseId, dateParams.courseWeeks);
      else selectCheapestCourse(tempCourses);

      UpdateCourse(dispatch, tempCourses);
    }

    // Accommodation
    const tempAccommodations = await fetchAccommodations(
      accommodationParams,
      dispatch
    );
    if (detail.needAccommodation === true)
      if (tempAccommodations) {
        selectCheapestAccommodation(tempAccommodations);

        if (params.bedroomType !== undefined)
          toggleAccommodationsBedroomType(
            tempAccommodations,
            params.bedroomType
          );

        if (params.accommodationId !== undefined)
          selectAccommodationById(tempAccommodations, params.accommodationId);

        UpdateAccommodation(dispatch, tempAccommodations);
      }

    // Additional
    const tempAdditionals = await fetchAdditionalServices(
      additionalServiceParam,
      dispatch
    );
    if (tempCourses) {
      UpdateAdditionalService(dispatch, tempAdditionals);
    }
  };

  const organizeSchoolParameters = async () => {
    const courseParams = {
      SchoolId: parseInt(params.id, 10),
      StartDate: new Date(dateParams.courseStart).toISOString(),
      WeeksQuantity: parseInt(dateParams.courseWeeks, 10),
      id: 0, // Id do curso/acomodação, se != 0 retorna apenas o curso/acomodação selecionado
    };

    const accommodationParams = {
      SchoolId: parseInt(params?.id, 10),
      StartDate: new Date(dateParams?.accommodationStart).toISOString(),
      EndDate: new Date(dateParams?.accommodationEnd).toISOString(),
      id: 0, // Id do curso/acomodação, se != 0 retorna apenas o curso/acomodação selecionado
    };

    const additionalServiceParam = {
      SchoolId: parseInt(params?.id, 10),
      StartDate: new Date(dateParams?.courseStart).toISOString(),
      WeekQuantity: parseInt(dateParams?.courseWeeks, 10),
    };

    await updatePageOptions(
      courseParams,
      accommodationParams,
      additionalServiceParam
    );
  };

  const updateUrl = () => {
    const { id, slugName, location, courses, accommodationGroups, weeks } =
      detail;

    const weekQuantity = weeks.some((w) => w === dateParams.courseWeeks)
      ? dateParams.courseWeeks
      : weeks[0];

    const queryParams = {
      accommodationId: getSelectedAccommodationId(accommodationGroups),
      bedroomType: getSelectedAccommodationBedroomType(accommodationGroups),
      courseId: getSelectedCourseId(courses),
      startDate: formatDate(dateParams?.courseStart, 'iso-string'),
      weekRange: weekQuantity,
    };

    const routerQueryClone = { ...router.query };
    delete routerQueryClone?.id;

    const url = buildUrl(
      [
        location?.city?.country?.slugName,
        location?.city?.slugName,
        slugName,
        id,
      ],
      { ...routerQueryClone, ...queryParams }
    );

    window.history.pushState(new Date(), '', url);
  };

  // useEffect's: Foram colocados aqui devido a sequência de carregamentos da página (Necessita solução melhor)
  useEffect(() => {
    setIsFetching(true);
    organizeSchoolParameters().then(() => {
      setIsFetching(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateParams, dateParams.courseWeeks]);

  useEffect(() => {
    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  useEffect(() => {
    async function getMainImage() {
      params.startDate = abroadDateSanitization(params.startDate);
      const response = await getMainSchoolImage(detail?.id);

      setMainImage(response.url);
    }

    getMainImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box bgcolor="background.paper">
      <Head>
        <title>{title}</title>
        <meta
          content="Fluencypass - Seu intercâmbio sem taxa de agência"
          key="title"
          property="og:title"
        />

        <meta
          content="O jeito mais fácil para fazer seu intercâmbio. Seu intercâmbio com o preço direto da escola sem as taxas das agências!"
          key="description"
          property="og:description"
        />

        <meta
          name="description"
          content={`Compare escolas em ${detail?.location?.city?.name}. Até 40% off, sem taxa de agência, suporte e assessoria grátis.`}
        />
        <meta
          property="og:description"
          content={`Compare escolas em ${detail?.location?.city?.name}. Até 40% off, sem taxa de agência, suporte e assessoria grátis.`}
          key="description"
        />
      </Head>

      <Header menuNavHeader={DefaultMenuNav} menuNavSideMenu={DefaultMenuNav} />

      {detail?.rating?.average > 0 && (
        // eslint-disable-next-line @next/next/no-before-interactive-script-outside-document
        <Script
          id="google-rating-schools"
          type="application/ld+json"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: generateReviewSchema(detail),
          }}
        />
      )}
      <Snackbar
        autoHideDuration={5000}
        onClose={handleCloseErrorMessage}
        open={showErrorMessage}
      >
        <MuiAlert
          elevation={6}
          onClose={handleCloseErrorMessage}
          severity="error"
          variant="filled"
        >
          {errorMessage}
        </MuiAlert>
      </Snackbar>

      <Backdrop sx={{ zIndex: 999, color: '#FFF' }} open={showLoader}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ScarcityVacancyComponent />

      <Container maxWidth="lg" sx={{ my: 2 }}>
        <SchoolBreadcrumbComponent detail={detail} />

        {!mainImage ? (
          <SkeletonFull />
        ) : (
          <Box
            sx={{
              backgroundImage: `url(${mainImage})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              cursor: 'pointer',
              height: { xs: '240px', sm: '350px' },
              width: '100%',
              position: 'relative',
            }}
            onClick={handleOpenGallery}
          >
            <Button
              sx={{
                fontWeight: 600,
                position: 'absolute',
                right: 24,
                textTransform: 'capitalize',
                top: 24,
                backgroundColor: '#f6f7f7',
                color: '#2A1D1D',

                '&:hover': {
                  backgroundColor: '#f6f7f7',
                },
              }}
              variant="contained"
              onClick={handleOpenGallery}
            >
              Ver fotos
            </Button>
          </Box>
        )}

        {openGallery && (
          <SchoolGalleryComponent
            id={detail?.id}
            schoolName={detail?.fantasyName}
            open={openGallery}
            handleClose={handleOpenGallery}
          />
        )}

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: ['column', 'row'],
            marginTop: '24px',
          }}
        >
          <Typography
            component="h1"
            sx={{ fontSize: '32px', fontWeight: 900, textAlign: 'center' }}
          >
            {detail.fantasyName}
          </Typography>
          <Box component="span" ml={1}>
            <LabelWithDescriptionComponent
              categories={categorySlug[detail.category]}
              categoriesSlugInfo={categoriesSlugInfo[detail.category]}
              commissionCategory={detail?.commissionCategory}
              isSalesExecutive={isSalesExecutive}
            />
          </Box>
        </Box>

        <Box my={2}>
          <Box
            sx={{
              height: showFullInformation ? '100%' : '96px',
              overflow: 'hidden',
              maskImage: showFullInformation
                ? 'none'
                : 'linear-gradient(180deg, #000 20%, transparent)',
            }}
          >
            <Collapse
              in={showFullInformation}
              collapsedSize={96}
              collapsedheight={96}
            >
              {aboutSchool}
              {ReactHtmlParser(`
              <p>
                Na <strong>Fluencypass</strong> você pode fazer seu
                <strong>orçamento online</strong>, comparar as
                <strong>escolas de intercâmbio</strong> e
                <strong>reservar seu intercâmbio no exterior</strong> com a ajuda
                dos nossos especialistas sem pagar nada a mais por isso, com zero
                taxa de agenciamento e garantia de <strong>melhor preço</strong>.
                Monte seu intercâmbio abaixo agora mesmo.
              </p>
            `)}
            </Collapse>
          </Box>
          <Button
            color="primary"
            variant="outlined"
            sx={{
              borderColor: '#e7e9ea',
              color: '#2A1D1D',
              display: 'block',
              mt: 1,
              mx: 'auto',
              width: { xs: '100%', sm: '640px' },

              '&:hover': {
                backgroundColor: 'transparent',
                borderColor: '#a3abae',
              },
            }}
            onClick={handleShowFullInformation}
          >
            Ver {showFullInformation ? 'menos' : 'mais'} informações sobre a
            escola
          </Button>
        </Box>

        {isSalesExecutive && detail?.privateInformation && (
          <Box bgcolor="background.secondary.main" p={2}>
            <Typography variant="subtitle1">
              <strong>Informação adicional</strong>
            </Typography>

            {ReactHtmlParser(detail.privateInformation)}
          </Box>
        )}

        <Box mt={2} id="builder">
          <Grid
            container
            spacing={2}
            direction={mobileBreakpoint ? 'column-reverse' : 'row'}
          >
            <Grid item md={8} xs={12}>
              {detail?.isActive && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <BuildPackageComponent
                      dateHandler={{ dateParams, setDateParams }}
                    />
                  </Grid>
                </Grid>
              )}

              <StepsComponent
                courseWeeks={dateParams.courseWeeks}
                courseStart={dateParams.courseStart}
              />

              {detail?.imagePath && (
                <VideoComponent imagePath={detail.imagePath} />
              )}

              {address && <SchoolLocationComponent address={address} />}
            </Grid>

            {detail.isActive ? (
              <Grid item sm={4} xs={12}>
                <Summary
                  createOrder={createAbroad}
                  dateParams={dateParams}
                  isFetching={isFetching}
                  isSalesExecutive={isSalesExecutive}
                />
              </Grid>
            ) : (
              <Grid item md={4} sm={8} xs={12}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 800,
                    marginBottom: '8px',
                    textAlign: 'center',
                  }}
                >
                  Desbloquear preços
                </Typography>

                <Box border={1} borderRadius="10px" px={1}>
                  <LeadFormComponent id="school-id-widget" />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>

      {detail?.comments?.length > 0 && (
        <SchoolTestimoniesComponent
          comments={detail.comments}
          rating={detail.rating}
        />
      )}

      <Box sx={{ bgcolor: 'background.secondary.main', mt: 8, pt: 1, pb: 8 }}>
        <Container maxWidth="lg">
          <DynamicFaqComponent
            dynamicFaqText="Perguntas frequentes"
            showButton={false}
            dynamicFaqTopics={abroadFaq}
          />
        </Container>
      </Box>

      <Footer maxWidth="lg" showCountries />

      <input
        id="hcountry"
        name="hcountry"
        type="hidden"
        value={detail?.location?.city?.country?.slugName}
      />
      <input
        id="hcity"
        name="hcity"
        type="hidden"
        value={detail?.location?.city?.slugName}
      />
      <input
        id="hschoolname"
        name="hschoolname"
        type="hidden"
        value={detail?.slugName}
      />
      <input type="hidden" id="hschoolid" name="hschoolid" value={detail?.id} />
    </Box>
  );
}

export const getServerSideProps = storeWrapper.getServerSideProps(
  async ({ req, res, store, query }) => {
    const isSalesExecutive = await hasPermissionByRole(req, res, 'expert');

    await fetchCountries(store.dispatch);
    await fetchSchoolDataFilter(store.dispatch);

    let searchParams = getSearchParameters({
      ...query,
      id: query?.id[3],
    });

    if (query.bedroomType > 0)
      searchParams = { ...searchParams, bedroomType: query.bedroomType };

    const schoolDetail = await fetchSchoolDetail(searchParams, store.dispatch);

    if (!schoolDetail?.id) {
      return {
        notFound: true,
      };
    }

    return {
      props: { params: searchParams, isSalesExecutive },
    };
  }
);

SchoolPage.propTypes = {
  params: PropTypes.instanceOf(Object),
  isSalesExecutive: PropTypes.bool,
};

SchoolPage.defaultProps = {
  params: {},
  isSalesExecutive: false,
};

export default SchoolPage;
